import {
  parsePositionKey,
  RogPositionResultCamel,
} from '../pages/ground-truth/components/rogStatusUtils';

const buildKey = (mod: number, shelf: number, column: number) => `${mod},${shelf},${column}`;

export const buildPositionKey = buildKey;

/**
 * Collect the set of occupied column numbers on a single (mod, shelf).
 */
const occupiedColumns = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  mod: number,
  shelf: number
): Set<number> => {
  const columns = new Set<number>();
  for (const key of Object.keys(rogResultsByPosition)) {
    const parsed = parsePositionKey(key);
    if (!parsed || parsed.mod !== mod || parsed.shelf !== shelf) continue;
    columns.add(parsed.column);
  }
  return columns;
};

/**
 * Remove the position at (mod, shelf, removedColumn). Only the contiguous block
 * of occupied columns directly above the removed one is shifted down by one;
 * the shift stops at the first gap so non-consecutive numbering beyond the gap
 * is preserved. Other shelves and mods are untouched.
 *
 * e.g. shelf has columns {1, 3, 4} and we remove column 1: column 3 is not
 * contiguous with the removed slot (column 2 is a gap), so nothing shifts and
 * the result is {3, 4}. Removing column 3 collapses {3, 4} down to {3}.
 */
export const removeRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  mod: number,
  shelf: number,
  removedColumn: number
): Record<string, RogPositionResultCamel> => {
  const columns = occupiedColumns(rogResultsByPosition, mod, shelf);
  if (!columns.has(removedColumn)) return rogResultsByPosition;

  // Find the first gap strictly above the removed column. Columns in
  // (removedColumn, firstGap) form the contiguous block that shifts down.
  let firstGapAbove = removedColumn + 1;
  while (columns.has(firstGapAbove)) firstGapAbove += 1;

  const next: Record<string, RogPositionResultCamel> = {};
  for (const [key, value] of Object.entries(rogResultsByPosition)) {
    const parsed = parsePositionKey(key);
    if (!parsed || parsed.mod !== mod || parsed.shelf !== shelf) {
      next[key] = value;
      continue;
    }
    if (parsed.column === removedColumn) continue;
    if (parsed.column > removedColumn && parsed.column < firstGapAbove) {
      next[buildKey(mod, shelf, parsed.column - 1)] = value;
    } else {
      next[key] = value;
    }
  }
  return next;
};

/**
 * Move a position from (fromMod, fromShelf, fromColumn) to (toMod, toShelf, toColumn).
 * Preserves the value at the source slot. Source shelf is shifted down (remove
 * semantics), then the destination shelf is shifted up to make room (add
 * semantics). Source and destination shelves can be the same.
 */
export const moveRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  fromMod: number,
  fromShelf: number,
  fromColumn: number,
  toMod: number,
  toShelf: number,
  toColumn: number
): Record<string, RogPositionResultCamel> => {
  const sourceKey = buildKey(fromMod, fromShelf, fromColumn);
  const value = rogResultsByPosition[sourceKey];
  if (!value) return rogResultsByPosition;
  if (fromMod === toMod && fromShelf === toShelf && fromColumn === toColumn) {
    return rogResultsByPosition;
  }
  const afterRemove = removeRogPosition(rogResultsByPosition, fromMod, fromShelf, fromColumn);
  return addRogPosition(afterRemove, toMod, toShelf, toColumn, value);
};

/**
 * Insert a new position at (mod, shelf, newColumn). If the target column is
 * empty it simply fills the gap and nothing else moves. If it is occupied, only
 * the contiguous block of occupied columns starting at newColumn is shifted up
 * by one to make room; the shift stops at the first gap, so positions beyond a
 * gap keep their numbers.
 *
 * e.g. shelf has columns {1, 3} and we add at column 2: column 2 is a gap, so
 * the result is {1, 2, 3} with column 3 untouched. Adding at column 1 with
 * {1, 2, 4} present shifts only {1, 2} up to {2, 3}, leaving column 4 alone.
 */
export const addRogPosition = (
  rogResultsByPosition: Record<string, RogPositionResultCamel>,
  mod: number,
  shelf: number,
  newColumn: number,
  value: RogPositionResultCamel
): Record<string, RogPositionResultCamel> => {
  const columns = occupiedColumns(rogResultsByPosition, mod, shelf);

  // Find the first gap at or above the target column. Columns in
  // [newColumn, firstGap) form the contiguous block that shifts up to make room.
  let firstGapAtOrAbove = newColumn;
  while (columns.has(firstGapAtOrAbove)) firstGapAtOrAbove += 1;

  const next: Record<string, RogPositionResultCamel> = {};
  for (const [key, existing] of Object.entries(rogResultsByPosition)) {
    const parsed = parsePositionKey(key);
    if (!parsed || parsed.mod !== mod || parsed.shelf !== shelf) {
      next[key] = existing;
      continue;
    }
    if (parsed.column >= newColumn && parsed.column < firstGapAtOrAbove) {
      next[buildKey(mod, shelf, parsed.column + 1)] = existing;
    } else {
      next[key] = existing;
    }
  }
  next[buildKey(mod, shelf, newColumn)] = value;
  return next;
};
